import type React from "react";
import { useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import { EditarComentarioDialog } from "@/components/feedback/EditarComentarioDialog";
import { EliminarComentarioDialog } from "@/components/feedback/EliminarComentarioDialog";
import { aplicarErroresDeComentario } from "@/components/feedback/errores";
import { FiltrosDelTablero } from "@/components/feedback/FiltrosDelTablero";
import { FormularioDeComentario } from "@/components/feedback/FormularioDeComentario";
import { TarjetaDeComentario } from "@/components/feedback/TarjetaDeComentario";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAutoresDeFeedback,
  useCatalogosDeFeedback,
  useCrearComentario,
  useFeedback,
} from "@/hooks/useFeedback";
import { getApiErrorMessage } from "@/lib/api";
import { combinarEstados, estadoDeConsulta } from "@/lib/estadoDeConsulta";
import type { ComentarioDeFeedback } from "@/lib/feedback";

const COMENTARIOS_SKELETON = 3;

/**
 * Tablero de Feedback: dejar un comentario sobre el sistema, leer los del
 * equipo y gestionarlos.
 *
 * TRES reglas que la pantalla NO decide, solo dibuja:
 *
 * 1. Los comentarios son de TODO el equipo (lo sostiene
 *    `FeedbackController::index`; acá no hay filtro que lo contradiga).
 * 2. Quién puede editar, borrar o mover el estado de CADA fila viene en los
 *    `puede_*` que manda el servidor, y salen de la misma Policy que autoriza
 *    cada request. Por eso la pantalla no puede ofrecer un botón que termine
 *    en un 403.
 * 3. Los módulos, tipos y estados llegan de `GET /api/feedback/catalogos`. El
 *    valor se persiste y el servidor lo valida contra un enum, así que una
 *    copia local terminaría ofreciendo una opción que el API rechaza el día
 *    que nace una pantalla nueva.
 *
 * Los filtros viven en la URL y no en `useState` para que recargar, compartir
 * el enlace o volver con el botón "atrás" conserven lo que el usuario eligió.
 *
 * El título y los breadcrumbs viven en el `TopBar` (`lib/pageMeta.ts`).
 */
export function Feedback() {
  const [params, setParams] = useSearchParams();
  const [aEditar, setAEditar] = useState<ComentarioDeFeedback | null>(null);
  const [aEliminar, setAEliminar] = useState<ComentarioDeFeedback | null>(null);

  const filtroModulo = params.get("modulo") ?? "";
  const filtroTipo = params.get("tipo") ?? "";
  const filtroAutor = params.get("autor") ?? "";
  const pagina = Number(params.get("pagina")) || 1;

  const tablero = useFeedback({
    modulo: filtroModulo || undefined,
    tipo: filtroTipo || undefined,
    autorId: Number(filtroAutor) || undefined,
    pagina,
  });
  const catalogos = useCatalogosDeFeedback();
  const autores = useAutoresDeFeedback();
  const crear = useCrearComentario();

  // El tablero y los catálogos alimentan la MISMA pantalla: sin catálogos no
  // hay formulario, sin tablero no hay listado, y mostrar media pantalla
  // sobre un error de la otra mitad es lo que `combinarEstados` evita.
  //
  // Los AUTORES quedan fuera a propósito: si esa consulta falla, lo único que
  // se pierde son las opciones de UN filtro. Tirar abajo la pantalla entera
  // por eso sería desproporcionado.
  const estado = combinarEstados(estadoDeConsulta(tablero), estadoDeConsulta(catalogos));

  const total = tablero.data?.total ?? 0;
  const totalGeneral = tablero.data?.totalGeneral ?? 0;
  const porPagina = tablero.data?.porPagina ?? 25;
  const totalPaginas = tablero.data?.totalPaginas ?? 1;
  const desde = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const hasta = Math.min(pagina * porPagina, total);
  const hayFiltro = Boolean(filtroModulo || filtroTipo || filtroAutor);

  /**
   * Cambiar un filtro vuelve SIEMPRE a la página 1: quedarse en la 3 después
   * de filtrar deja el tablero vacío sobre un resultado que sí tiene filas, y
   * se lee como "no hay comentarios de este módulo".
   */
  function cambiarFiltro(clave: "modulo" | "tipo" | "autor", valor: string) {
    const siguiente = new URLSearchParams(params);
    if (valor) siguiente.set(clave, valor);
    else siguiente.delete(clave);
    siguiente.delete("pagina");
    setParams(siguiente);
  }

  function limpiarFiltros() {
    const siguiente = new URLSearchParams(params);
    for (const clave of ["modulo", "tipo", "autor", "pagina"]) siguiente.delete(clave);
    setParams(siguiente);
  }

  function urlDePagina(n: number): string {
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    return `?${siguiente.toString()}`;
  }

  function irAPagina(evento: React.MouseEvent, n: number) {
    // Los controles son enlaces REALES (con href) para que el teclado y el
    // clic con rueda funcionen; el `preventDefault` evita la recarga completa
    // y deja la navegación del lado del cliente.
    evento.preventDefault();
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    setParams(siguiente);
  }

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div>
        <p className="text-muted-foreground text-caption">
          Cuéntanos qué falló, qué mejorarías o qué necesitas saber del sistema.
        </p>
        <p className="text-ink-hint mt-0.5 max-w-[70ch] text-xs">
          Los comentarios los ve todo el equipo, así que revisa el tablero antes de escribir:
          si alguien ya reportó lo mismo, no hace falta repetirlo. Elige bien el tipo — es lo
          que decide si algo se arregla, se agenda o se responde.
        </p>
      </div>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el tablero"
          error={tablero.error ?? catalogos.error}
          onReintentar={() => {
            void tablero.refetch();
            void catalogos.refetch();
          }}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="los comentarios del equipo" />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,520px)_1fr]">
          {/* Alta. `section` con nombre y no un `div`: la barra de filtros de
              al lado también tiene controles rotulados "Módulo" y "Tipo", y
              sin dos regiones nombradas no hay forma de decir cuál es cuál —
              ni leyendo la pantalla ni desde un test. */}
          <section aria-label="Dejar un comentario" className="bg-card rounded-xl border p-6">
            <FormularioDeComentario
              modulos={catalogos.data?.modulos ?? []}
              tipos={catalogos.data?.tipos ?? []}
              etiquetaEnvio="Enviar comentario"
              enviando={crear.isPending}
              onEnviar={(valores, acciones) =>
                crear.mutate(valores, {
                  onSuccess: () => {
                    toast.success("Gracias — tu comentario quedó registrado.");
                    acciones.limpiar();
                    // Vuelve a la primera página y saca los filtros: el
                    // comentario recién enviado entra arriba, y con un filtro
                    // puesto o en la página 3 quedaría fuera de la vista justo
                    // después de mandarlo — se lee como que no se guardó.
                    if (pagina > 1 || hayFiltro) limpiarFiltros();
                  },
                  onError: (error) => {
                    // Un 422 se pinta en el campo que lo causó: es la única
                    // forma de que la persona sepa QUÉ corregir del texto que
                    // acaba de escribir. Lo demás no tiene campo donde
                    // escribirse y va al toast.
                    if (aplicarErroresDeComentario(error, acciones)) return;
                    toast.error(
                      getApiErrorMessage(
                        error,
                        "No pudimos enviar tu comentario. Inténtalo de nuevo.",
                      ),
                    );
                  },
                })
              }
            />
          </section>

          {/* Tablero */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-extrabold tracking-tight">Comentarios del equipo</h2>
              <p className="text-ink-hint text-xs">
                {total === 0 ? "Mostrando 0" : `Mostrando ${desde}–${hasta} de ${total}`}
                {/* El total sin filtrar solo aparece cuando hay filtro: si no,
                    repetiría el número de al lado. */}
                {hayFiltro ? ` · ${totalGeneral} en total` : ""}
              </p>
            </div>

            <FiltrosDelTablero
              modulos={catalogos.data?.modulos ?? []}
              tipos={catalogos.data?.tipos ?? []}
              autores={autores.data ?? []}
              valores={{ modulo: filtroModulo, tipo: filtroTipo, autor: filtroAutor }}
              onCambiar={cambiarFiltro}
              onLimpiar={limpiarFiltros}
            />

            {estado.cargando ? (
              Array.from({ length: COMENTARIOS_SKELETON }).map((_, i) => (
                // El `role`/`aria-label` es lo que deja que un test afirme que
                // la carga se dibuja (y que borrar esta rama ponga la suite en
                // rojo).
                <div
                  key={i}
                  role="status"
                  aria-label="Cargando comentarios"
                  className="bg-card space-y-3 rounded-xl border p-5"
                >
                  <div className="flex items-center gap-2.5">
                    <Skeleton className="size-8 rounded-full" />
                    <Skeleton className="h-3.5 w-40" />
                  </div>
                  <Skeleton className="h-3.5 w-full" />
                  <Skeleton className="h-3.5 w-3/4" />
                </div>
              ))
            ) : total === 0 ? (
              /* Dos vacíos distintos: "todavía nadie escribió" y "tu filtro no
                 encontró nada" NO son lo mismo, y decirle a alguien que el
                 tablero está vacío cuando lo que hizo fue filtrar de más lo
                 manda a escribir un comentario duplicado. */
              <Empty className="rounded-xl border">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MessageSquarePlus />
                  </EmptyMedia>
                  <EmptyTitle>
                    {hayFiltro ? "Ningún comentario con esos filtros" : "Todavía no hay comentarios"}
                  </EmptyTitle>
                  <EmptyDescription>
                    {hayFiltro
                      ? `Hay ${totalGeneral} comentario${totalGeneral === 1 ? "" : "s"} en el tablero. Prueba con «Limpiar filtros» para verlos todos.`
                      : "Sé el primero: usa el formulario de al lado para contar qué falló, qué mejorarías o qué necesitas saber."}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              tablero.data?.comentarios.map((comentario) => (
                <TarjetaDeComentario
                  key={comentario.id}
                  comentario={comentario}
                  estados={catalogos.data?.estados ?? []}
                  onEditar={setAEditar}
                  onEliminar={setAEliminar}
                />
              ))
            )}

            {totalPaginas > 1 && (
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href={urlDePagina(pagina - 1)}
                      aria-label="Página anterior"
                      aria-disabled={pagina === 1}
                      className={pagina === 1 ? "pointer-events-none opacity-50" : undefined}
                      onClick={(evento) => irAPagina(evento, pagina - 1)}
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                    <PaginationItem key={n}>
                      <PaginationLink
                        href={urlDePagina(n)}
                        isActive={n === pagina}
                        onClick={(evento) => irAPagina(evento, n)}
                      >
                        {n}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      href={urlDePagina(pagina + 1)}
                      aria-label="Página siguiente"
                      aria-disabled={pagina === totalPaginas}
                      className={
                        pagina === totalPaginas ? "pointer-events-none opacity-50" : undefined
                      }
                      onClick={(evento) => irAPagina(evento, pagina + 1)}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </div>
      )}

      <EditarComentarioDialog
        comentario={aEditar}
        modulos={catalogos.data?.modulos ?? []}
        tipos={catalogos.data?.tipos ?? []}
        onCerrar={() => setAEditar(null)}
      />
      <EliminarComentarioDialog
        comentario={aEliminar}
        onCerrar={() => setAEliminar(null)}
      />
    </div>
  );
}
